/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { StaffForm } from 'app/components/staff/StaffForm'
import { useIsOrgUser } from 'app/hooks/useIsOrgUser'
import { useDeleteUser } from 'app/hooks/useDeleteUser'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { RoleJson } from 'app/types/entities'
import { User, UserJson } from 'app/types/entities'
import { WorkspaceJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { setCookie } from 'lib/setCookie.client'
import { useUserStore, useRoleStore, useWorkspaceStore } from 'app/stores/StoreContext'
import { graphql } from 'app/generated/gql'
import { gqlFetchList, gqlMutation } from 'app/lib/graphqlClient'
import { opsClient } from 'app/lib/opsUrqlClient'
import { buildCreateUserInput, buildUpdateUserInput } from './userDetailsSubmit'

const IMPERSONATE_USER_MUTATION = graphql(`
  mutation ImpersonateUser($userId: String!) {
    impersonateUser(userId: $userId) {
      accessToken
    }
  }
`)

const CREATE_USER_MUTATION = graphql(`
  mutation CreateUserFromDetails($input: CreateUserInput!) {
    createUser(input: $input) {
      id
    }
  }
`)

const UPDATE_USER_MUTATION = graphql(`
  mutation UpdateUserFromDetails($id: ID!, $input: UpdateUserInput!) {
    updateUser(id: $id, input: $input) {
      id
    }
  }
`)

// The public updateUser mutation has no `type` field, so promoting/demoting a
// user between super and standard goes through the super-only ops schema
// (TRK-6126). Mirrors the legacy super-only control.
const OPS_SET_USER_TYPE_MUTATION = graphql(`
  mutation OpsSetUserType($userId: ID!, $type: String!) {
    opsSetUserType(userId: $userId, type: $type) {
      id
      type
    }
  }
`)

export type UseUserDetailsReturn = {
  user: UserJson & { _aliases?: string }
  me: UserJson
  userId: string | undefined
  isNewUser: boolean
  isEditingSelf: boolean
  workspaceRoles: RoleJson[]
  canManageUsers: boolean
  userIsOrg: boolean
  currentWorkspace: WorkspaceJson | undefined
  workspace: WorkspaceJson | undefined
  setWorkspace: (ws: WorkspaceJson | undefined) => void
  defaultValues: UserJson & { role: number }
  allowedWorkspaces: WorkspaceJson[]
  setAllowedWorkspaces: React.Dispatch<React.SetStateAction<WorkspaceJson[]>>
  allWorkspaces: boolean
  setAllWorkspaces: (v: boolean) => void
  isDeleting: boolean
  position: User['position'] | undefined
  setPosition: (p: User['position'] | undefined) => void
  role: number
  setRole: (r: number) => void
  roleId: string
  setRoleId: (r: string) => void
  userType: User['type']
  setUserType: (t: User['type']) => void
  isDirtyForm: boolean
  setIsDirtyForm: (v: boolean) => void
  selectedStaff: { id: string; name: string; value: string } | undefined
  setSelectedStaff: (s: { id: string; name: string; value: string } | undefined) => void
  formRef: React.RefObject<HTMLFormElement | null>
  locationHiddenInputs: Array<{ name: string; value: string }>
  handleLocationHiddenInputsChange: (inputs: Array<{ name: string; value: string }>) => void
  canDelete: boolean
  deleteUserHandler: () => Promise<void>
  impersonateUserHandler: () => void
  onAllowedWorkspaceChange: (workspace: WorkspaceJson) => void
  removeAllowedWorkspace: (workspace: WorkspaceJson) => void
  openStaffModal: () => void
  isNewWorkspaceUser: boolean
  onSubmit: (body?: Record<string, unknown>) => void
}

export function useUserDetails(): UseUserDetailsReturn {
  const currentWorkspace = useWorkspace()
  const navigate = useNavigate()
  const { userId } = useParams()
  const isNewUser = userId === `new`
  const me = useUser()
  const isOrgUser = useIsOrgUser()

  const checkPermission = useCheckPermission()
  const canManageUsers = checkPermission([`org:settings:manage-users`, `users:manage`])
  const roleStore = useRoleStore()
  const workspaceStore = useWorkspaceStore()
  const userStore = useUserStore()

  const [user, setUser] = useState<UserJson>({} as UserJson)
  const [workspaceRoles, setWorkspaceRoles] = useState<RoleJson[]>([])

  const [allowedWorkspaces, setAllowedWorkspaces] = useState<WorkspaceJson[]>(
    currentWorkspace ? [currentWorkspace as WorkspaceJson] : [],
  )
  const [workspace, setWorkspace] = useState<WorkspaceJson | undefined>()
  const [allWorkspaces, setAllWorkspaces] = useState(false)
  const { deleteUser, isDeleting } = useDeleteUser()
  const [position, setPosition] = useState<User['position'] | undefined>()
  const [confirm] = useConfirmation()
  const [role, setRole] = useState<number>(1)
  const [roleId, setRoleId] = useState<string>(``)
  const [userType, setUserType] = useState<User['type']>(`standard`)
  const isNewWorkspaceUser = !!(isNewUser && currentWorkspace)
  const [isDirtyForm, setIsDirtyForm] = useState<boolean>(false)
  const [selectedStaff, setSelectedStaff] = useState<{ id: string; name: string; value: string } | undefined>()
  const formRef = useRef<HTMLFormElement>(null)
  const [locationHiddenInputs, setLocationHiddenInputs] = useState<Array<{ name: string; value: string }>>([])
  const isEditingSelf = !isNewUser && me?.id === user.id

  const handleLocationHiddenInputsChange = (inputs: Array<{ name: string; value: string }>) => {
    setLocationHiddenInputs(inputs)
  }

  // Fetch user data
  useEffect(() => {
    if (isNewUser || !userId) return
    userStore
      .fetchOne(userId)
      .then((result) => {
        if (result.data) {
          const data = result.data as any
          const u = data.user as UserJson
          setUser(u)
          setWorkspaceRoles(data.workspaceRoles ?? [])
          setWorkspace(data.workspace)
          setPosition(u.position)
          setRole(u.role ?? 1)
          setRoleId(u.roleId ?? ``)
          setUserType(u.type ?? `standard`)
          if (u.assignedWorkspaceIds?.length) {
            workspaceStore
              .fetchList({
                search: u.assignedWorkspaceIds.join(`,`),
                pagination: { page: 0, limit: 100 },
              })
              .then((wsResult) => {
                const wsData = wsResult.data as any
                setAllowedWorkspaces(wsData?.workspaces?.hits ?? wsData?.hits ?? [])
              })
              .catch(() => {})
          }
        }
      })
      .catch((err) => logError(`Error fetching user details`, err))
  }, [userId])

  // Fetch workspace roles for new users (existing user path gets them from userStore.fetchOne)
  useEffect(() => {
    if (!isNewUser && workspaceRoles.length > 0) return
    roleStore.fetchList({ pagination: { page: 0, limit: 100 } }).then((result) => {
      const data = result.data as any
      const roles = data?.roles?.hits ?? []
      if (roles.length > 0) setWorkspaceRoles(roles)
    })
  }, [isNewUser])

  useEffect(() => {
    if (isNewWorkspaceUser) {
      setAllowedWorkspaces([{ ...currentWorkspace } as WorkspaceJson])
    }
  }, [currentWorkspace])

  useEffect(() => {
    if (user.id && !isNewUser) {
      bg(async () => {
        const result = await gqlFetchList<{ id: string; name: string; userId: string }>(
          'users',
          { search: `type:staff AND userId:${user.id}` },
          'id name userId',
        )
        if (result.hits.length > 0) {
          const staff = result.hits[0]
          setSelectedStaff({ id: staff.id!, name: staff.name ?? ``, value: staff.id! })
        } else {
          setSelectedStaff(undefined)
        }
      })
    }
  }, [user.id, isNewUser])
  useEffect(() => {
    const handleInput = () => setIsDirtyForm(true)
    formRef.current?.addEventListener(`input`, handleInput)
    return () => {
      formRef.current?.removeEventListener(`input`, handleInput)
    }
  }, [formRef.current])

  const defaultValues = { ...user, role: user.role || 1 }

  const onAllowedWorkspaceChange = (ws: WorkspaceJson) => {
    if (ws.id === `0`) return
    if (allowedWorkspaces.some((aw) => aw.id === ws.id)) {
      removeAllowedWorkspace(ws)
    } else {
      setAllowedWorkspaces((prev) => [...prev, ws])
      setWorkspace(ws)
    }
    setIsDirtyForm(true)
  }

  const removeAllowedWorkspace = (aw: WorkspaceJson) => {
    const workspaces = allowedWorkspaces.filter((item) => item.id !== aw.id)
    setAllowedWorkspaces(workspaces)
    setWorkspace(workspaces.length ? workspaces[0] : undefined)
    setIsDirtyForm(true)
  }

  const canDelete = useMemo(() => {
    if (isNewUser) return false
    if (me?.type === `super`) return true
    return canManageUsers
  }, [me, user, canManageUsers])

  const deleteUserHandler = () => deleteUser(userId!)

  const impersonateUserHandler = () => {
    if (!userId) return
    confirm({
      title: `Impersonate User`,
      description: `Are you sure you want to impersonate this user? Be responsible with your actions.`,
      variant: `danger`,
    })
      .then(() => setCookie(`hidePhi`, false))
      .then(() => gqlMutation(IMPERSONATE_USER_MUTATION, { userId }))
      .then((result) => {
        if (result.error) {
          notifyError(result.error.message)
          return
        }
        if (result.data?.impersonateUser?.accessToken) {
          window.location.href = `/`
        } else {
          notifyError(`Impersonation failed: no token received`)
        }
      })
      .catch((err) => notifyError(err.message))
  }

  const openStaffModal = () =>
    showModal(
      <StaffForm
        onSubmit={(data) => {
          if (data.staff) {
            setSelectedStaff({ id: data.staff.id!, name: data.staff.name ?? ``, value: data.staff.id! })
            setIsDirtyForm(true)
          }
        }}
        hideAssociatedUser
      />,
      { title: `Add Staff`, isFullScreen: false, removePadding: true, useRadix: true },
    )

  // Submit handler restored after the Remix-to-SPA migration dropped the route's
  // action (TRK-6070). A new user is created through the createUser mutation; an
  // existing user is updated through updateUser. The old server action also
  // linked staff and sent invites — that broader flow is out of scope here.
  const onSubmit = (body?: Record<string, unknown>) => {
    if (!body) return
    const fallbackRoleId = roleId || undefined
    const withStaff = (b: Record<string, unknown>) => ({ staffId: selectedStaff?.id, ...b })

    if (isNewUser) {
      const input = buildCreateUserInput(withStaff(body), fallbackRoleId)
      if (!input) {
        notifyError(`Name and a valid email are required`)
        return
      }
      gqlMutation(CREATE_USER_MUTATION, { input })
        .then(async (result) => {
          if (result.error) {
            notifyError(result.error.message)
            return
          }
          const created = result.data?.createUser
          if (!created?.id) {
            notifyError(`Failed to create user`)
            return
          }
          const submittedType = body.type
          if (me?.type === `super` && submittedType === `super`) {
            const typeResult = await gqlMutation(
              OPS_SET_USER_TYPE_MUTATION,
              { userId: created.id, type: submittedType },
              { client: opsClient },
            )
            if (typeResult.error) {
              notifyError(typeResult.error.message)
              return
            }
          }
          setIsDirtyForm(false)
          notifySuccess(`User created`)
          navigate(`/settings/users`)
        })
        .catch((err) => notifyError(err.message))
      return
    }

    const input = buildUpdateUserInput(body, fallbackRoleId)
    gqlMutation(UPDATE_USER_MUTATION, { id: userId!, input })
      .then(async (result) => {
        if (result.error) {
          notifyError(result.error.message)
          return
        }
        const submittedType = body.type
        if (
          me?.type === `super` &&
          (submittedType === `super` || submittedType === `standard`) &&
          submittedType !== user.type
        ) {
          const typeResult = await gqlMutation(
            OPS_SET_USER_TYPE_MUTATION,
            { userId: userId!, type: submittedType },
            { client: opsClient },
          )
          if (typeResult.error) {
            notifyError(typeResult.error.message)
            return
          }
        }
        setIsDirtyForm(false)
        notifySuccess(`User saved`)
        navigate(`/settings/users`)
      })
      .catch((err) => notifyError(err.message))
  }

  return {
    user,
    me: me!,
    userId,
    isNewUser,
    isEditingSelf,
    workspaceRoles,
    canManageUsers,
    userIsOrg: isOrgUser,
    currentWorkspace: currentWorkspace as WorkspaceJson | undefined,
    workspace,
    setWorkspace,
    defaultValues,
    allowedWorkspaces,
    setAllowedWorkspaces,
    allWorkspaces,
    setAllWorkspaces,
    isDeleting,
    position,
    setPosition,
    role,
    setRole,
    roleId,
    setRoleId,
    userType,
    setUserType,
    isDirtyForm,
    setIsDirtyForm,
    selectedStaff,
    setSelectedStaff,
    formRef,
    locationHiddenInputs,
    handleLocationHiddenInputsChange,
    canDelete,
    deleteUserHandler,
    impersonateUserHandler,
    onAllowedWorkspaceChange,
    removeAllowedWorkspace,
    openStaffModal,
    isNewWorkspaceUser,
    onSubmit,
  }
}
